import React, { useState, createElement } from 'react';
import { XIcon, PlusIcon, EditIcon, TrashIcon, SaveIcon, MapPinIcon, UserIcon, AlertCircleIcon, DownloadIcon, UploadIcon, DatabaseIcon, SettingsIcon, CheckCircleIcon, PhoneIcon, ClockIcon, FuelIcon, ShieldIcon } from 'lucide-react';
import { GasStation } from '../data/stations';
import { User, UserRole } from '../types/user';
import { exportDatabase, importDatabase, clearDatabase } from '../utils/database';
interface SettingsModalProps {
  onClose: () => void;
  stations: GasStation[];
  onAddStation: (station: Omit<GasStation, 'id'>) => void;
  onUpdateStation: (stationId: string, updates: Partial<GasStation>) => void;
  users: User[];
  onAddUser: (user: Omit<User, 'id'>) => void;
  onUpdateUser: (userId: string, updates: Partial<User>) => void;
  onDeleteUser: (userId: string) => void;
  currentUserId: string;
}
export function SettingsModal({
  onClose,
  stations,
  onAddStation,
  onUpdateStation,
  users,
  onAddUser,
  onUpdateUser,
  onDeleteUser,
  currentUserId
}: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<'stations' | 'users' | 'database'>('stations');
  const [stationSubTab, setStationSubTab] = useState<'list' | 'add'>('list');
  const [userSubTab, setUserSubTab] = useState<'list' | 'add'>('list');
  const [editingStation, setEditingStation] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [stationFormData, setStationFormData] = useState<Partial<GasStation>>({
    code: '',
    name: '',
    location: '',
    commune: '',
    pumps: 5,
    prices: {
      essence: 750,
      diesel: 680,
      gpl: 520
    },
    capacity: 30000,
    managerContact: '',
    fuelAvailability: {
      essence: true,
      diesel: true,
      gpl: false
    },
    openingHours: '06:00 - 22:00',
    isOperational: true
  });
  const [userFormData, setUserFormData] = useState<Partial<User>>({
    username: '',
    password: '',
    fullName: '',
    phone: '',
    address: '',
    role: 'operator',
    assignedStationId: undefined
  });
  // Station handlers
  const handleStationInputChange = (field: string, value: any) => {
    setStationFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handlePriceChange = (fuelType: 'essence' | 'diesel' | 'gpl', value: number) => {
    setStationFormData(prev => ({
      ...prev,
      prices: {
        ...prev.prices!,
        [fuelType]: value
      }
    }));
  };
  const handleAvailabilityChange = (fuelType: 'essence' | 'diesel' | 'gpl', value: boolean) => {
    setStationFormData(prev => ({
      ...prev,
      fuelAvailability: {
        ...prev.fuelAvailability!,
        [fuelType]: value
      }
    }));
  };
  const handleSubmitNewStation = (e: React.FormEvent) => {
    e.preventDefault();
    onAddStation(stationFormData as Omit<GasStation, 'id'>);
    resetStationForm();
    setStationSubTab('list');
  };
  const handleStartEditStation = (station: GasStation) => {
    setEditingStation(station.id);
    setStationFormData(station);
  };
  const handleSaveEditStation = () => {
    if (editingStation) {
      onUpdateStation(editingStation, stationFormData);
      setEditingStation(null);
      resetStationForm();
    }
  };
  const handleCancelEditStation = () => {
    setEditingStation(null);
    resetStationForm();
  };
  const resetStationForm = () => {
    setStationFormData({
      code: '',
      name: '',
      location: '',
      commune: '',
      pumps: 5,
      prices: {
        essence: 750,
        diesel: 680,
        gpl: 520
      },
      capacity: 30000,
      managerContact: '',
      fuelAvailability: {
        essence: true,
        diesel: true,
        gpl: false
      },
      openingHours: '06:00 - 22:00',
      isOperational: true
    });
  };
  // User handlers
  const handleUserInputChange = (field: string, value: any) => {
    setUserFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmitNewUser = (e: React.FormEvent) => {
    e.preventDefault();
    onAddUser(userFormData as Omit<User, 'id'>);
    resetUserForm();
    setUserSubTab('list');
  };
  const handleStartEditUser = (user: User) => {
    setEditingUser(user.id);
    setUserFormData({
      username: user.username,
      password: user.password,
      fullName: user.fullName,
      phone: user.phone || '',
      address: user.address || '',
      role: user.role,
      assignedStationId: user.assignedStationId
    });
  };
  const handleSaveEditUser = () => {
    if (editingUser) {
      onUpdateUser(editingUser, userFormData);
      setEditingUser(null);
      resetUserForm();
    }
  };
  const handleCancelEditUser = () => {
    setEditingUser(null);
    resetUserForm();
  };
  const resetUserForm = () => {
    setUserFormData({
      username: '',
      password: '',
      fullName: '',
      phone: '',
      address: '',
      role: 'operator',
      assignedStationId: undefined
    });
  };
  const handleExportDatabase = () => {
    try {
      const data = exportDatabase();
      const blob = new Blob([data], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cecogec-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      alert('✅ Base de données exportée avec succès!');
    } catch (error) {
      alert("❌ Erreur lors de l'exportation");
    }
  };
  const handleImportDatabase = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const content = e.target?.result as string;
        const success = importDatabase(content);
        if (success) {
          alert('✅ Base de données importée avec succès! La page va se recharger.');
          window.location.reload();
        } else {
          alert("❌ Erreur lors de l'importation");
        }
      } catch (error) {
        alert('❌ Fichier invalide');
      }
    };
    reader.readAsText(file);
  };
  const handleClearDatabase = () => {
    if (window.confirm('⚠️ ATTENTION: Cette action va effacer TOUTES les données!\n\nVoulez-vous vraiment continuer?')) {
      if (window.confirm("⚠️ DERNIÈRE CONFIRMATION: Toutes les files d'attente, l'historique et les données seront perdus!\n\nÊtes-vous absolument sûr?")) {
        clearDatabase();
        alert('✅ Base de données effacée. La page va se recharger.');
        window.location.reload();
      }
    }
  };
  const getRoleBadge = (role: UserRole) => {
    const roleConfig = {
      admin: {
        label: 'Administrateur',
        color: 'bg-purple-100 text-purple-800 border-purple-300'
      },
      supervisor: {
        label: 'Superviseur',
        color: 'bg-blue-100 text-blue-800 border-blue-300'
      },
      'general-supervisor': {
        label: 'Superviseur Général',
        color: 'bg-orange-100 text-orange-800 border-orange-300'
      },
      operator: {
        label: 'Opérateur',
        color: 'bg-green-100 text-green-800 border-green-300'
      }
    };
    const config = roleConfig[role];
    return <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${config.color}`} data-id="element-536">
        {config.label}
      </span>;
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-537">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden" data-id="element-538">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 p-6 text-white" data-id="element-539">
          <div className="flex items-center justify-between" data-id="element-540">
            <div className="flex items-center gap-3" data-id="element-541">
              <SettingsIcon className="w-8 h-8" data-id="element-542" />
              <div data-id="element-543">
                <h2 className="text-2xl font-bold" data-id="element-544">Paramétrage Système</h2>
                <p className="text-orange-100 text-sm" data-id="element-545">
                  Configuration des stations, utilisateurs et base de données
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors" data-id="element-546">
              <XIcon className="w-6 h-6" data-id="element-547" />
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50" data-id="element-548">
          <div className="flex gap-2 px-6" data-id="element-549">
            <button onClick={() => setActiveTab('stations')} className={`px-6 py-4 font-semibold transition-all ${activeTab === 'stations' ? 'text-orange-600 border-b-4 border-orange-600 bg-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-550">
              <div className="flex items-center gap-2" data-id="element-551">
                <MapPinIcon className="w-5 h-5" data-id="element-552" />
                <span data-id="element-553">Stations ({stations.length})</span>
              </div>
            </button>
            <button onClick={() => setActiveTab('users')} className={`px-6 py-4 font-semibold transition-all ${activeTab === 'users' ? 'text-orange-600 border-b-4 border-orange-600 bg-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-554">
              <div className="flex items-center gap-2" data-id="element-555">
                <UserIcon className="w-5 h-5" data-id="element-556" />
                <span data-id="element-557">Utilisateurs ({users.length})</span>
              </div>
            </button>
            <button onClick={() => setActiveTab('database')} className={`px-6 py-4 font-semibold transition-all ${activeTab === 'database' ? 'text-orange-600 border-b-4 border-orange-600 bg-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-558">
              <div className="flex items-center gap-2" data-id="element-559">
                <DatabaseIcon className="w-5 h-5" data-id="element-560" />
                <span data-id="element-561">Base de Données</span>
              </div>
            </button>
          </div>
        </div>
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]" data-id="element-562">
          {activeTab === 'database' && <div className="space-y-6" data-id="element-563">
              {/* Database Management Section */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200" data-id="element-564">
                <div className="flex items-center gap-3 mb-4" data-id="element-565">
                  <DatabaseIcon className="w-6 h-6 text-blue-600" data-id="element-566" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-567">
                    Gestion de la Base de Données
                  </h3>
                </div>
                <p className="text-sm text-gray-600 mb-6" data-id="element-568">
                  Exportez, importez ou réinitialisez toutes les données du
                  système
                </p>
                <div className="space-y-4" data-id="element-569">
                  {/* Export */}
                  <div className="bg-white rounded-lg p-4 border border-blue-200" data-id="element-570">
                    <div className="flex items-start justify-between" data-id="element-571">
                      <div className="flex-1" data-id="element-572">
                        <div className="flex items-center gap-2 mb-2" data-id="element-573">
                          <DownloadIcon className="w-5 h-5 text-green-600" data-id="element-574" />
                          <h4 className="font-bold text-gray-900" data-id="element-575">
                            Exporter les Données
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600" data-id="element-576">
                          Téléchargez une sauvegarde complète de toutes les
                          données (stations, utilisateurs, files d'attente,
                          historique)
                        </p>
                      </div>
                      <button onClick={handleExportDatabase} className="ml-4 flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors" data-id="element-577">
                        <DownloadIcon className="w-4 h-4" data-id="element-578" />
                        Exporter
                      </button>
                    </div>
                  </div>
                  {/* Import */}
                  <div className="bg-white rounded-lg p-4 border border-blue-200" data-id="element-579">
                    <div className="flex items-start justify-between" data-id="element-580">
                      <div className="flex-1" data-id="element-581">
                        <div className="flex items-center gap-2 mb-2" data-id="element-582">
                          <UploadIcon className="w-5 h-5 text-blue-600" data-id="element-583" />
                          <h4 className="font-bold text-gray-900" data-id="element-584">
                            Importer les Données
                          </h4>
                        </div>
                        <p className="text-sm text-gray-600" data-id="element-585">
                          Restaurez une sauvegarde précédente. Cela remplacera
                          toutes les données actuelles.
                        </p>
                      </div>
                      <label className="ml-4 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors cursor-pointer" data-id="element-586">
                        <UploadIcon className="w-4 h-4" data-id="element-587" />
                        Importer
                        <input type="file" accept=".json" onChange={handleImportDatabase} className="hidden" data-id="element-588" />
                      </label>
                    </div>
                  </div>
                  {/* Clear Database */}
                  <div className="bg-red-50 rounded-lg p-4 border-2 border-red-300" data-id="element-589">
                    <div className="flex items-start justify-between" data-id="element-590">
                      <div className="flex-1" data-id="element-591">
                        <div className="flex items-center gap-2 mb-2" data-id="element-592">
                          <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-593" />
                          <h4 className="font-bold text-red-900" data-id="element-594">
                            Réinitialiser la Base de Données
                          </h4>
                        </div>
                        <p className="text-sm text-red-700" data-id="element-595">
                          ⚠️ DANGER: Cette action supprimera TOUTES les données
                          de manière permanente. Cette action est irréversible!
                        </p>
                      </div>
                      <button onClick={handleClearDatabase} className="ml-4 flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors" data-id="element-596">
                        <TrashIcon className="w-4 h-4" data-id="element-597" />
                        Réinitialiser
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* Info Section */}
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200" data-id="element-598">
                <h4 className="font-bold text-gray-900 mb-3" data-id="element-599">
                  ℹ️ Informations sur le Stockage
                </h4>
                <ul className="space-y-2 text-sm text-gray-600" data-id="element-600">
                  <li className="flex items-start gap-2" data-id="element-601">
                    <span className="text-blue-600 font-bold" data-id="element-602">•</span>
                    <span data-id="element-603">
                      Les données sont stockées localement dans votre navigateur
                      (localStorage)
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-604">
                    <span className="text-blue-600 font-bold" data-id="element-605">•</span>
                    <span data-id="element-606">
                      Les données persistent même après déconnexion ou fermeture
                      du navigateur
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-607">
                    <span className="text-blue-600 font-bold" data-id="element-608">•</span>
                    <span data-id="element-609">
                      Exportez régulièrement vos données pour créer des
                      sauvegardes
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-610">
                    <span className="text-blue-600 font-bold" data-id="element-611">•</span>
                    <span data-id="element-612">
                      Les données sont automatiquement sauvegardées à chaque
                      modification
                    </span>
                  </li>
                </ul>
              </div>
            </div>}
          {activeTab === 'stations' ? <div data-id="element-613">
              {/* Station Sub-Tabs */}
              <div className="flex gap-2 mb-6" data-id="element-614">
                <button onClick={() => setStationSubTab('list')} className={`px-4 py-2 rounded-lg font-semibold transition-colors ${stationSubTab === 'list' ? 'bg-purple-100 text-purple-900' : 'bg-gray-100 text-gray-600'}`} data-id="element-615">
                  Liste des Stations
                </button>
                <button onClick={() => {
              setStationSubTab('add');
              setEditingStation(null);
              resetStationForm();
            }} className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${stationSubTab === 'add' ? 'bg-purple-100 text-purple-900' : 'bg-gray-100 text-gray-600'}`} data-id="element-616">
                  <PlusIcon className="w-4 h-4" data-id="element-617" />
                  Ajouter une Station
                </button>
              </div>
              {stationSubTab === 'list' ? <div className="space-y-4" data-id="element-618">
                  {stations.map(station => <div key={station.id} className={`border-2 rounded-xl p-5 ${editingStation === station.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`} data-id="element-619">
                      {editingStation === station.id ? <div className="space-y-4" data-id="element-620">
                          <div className="flex items-center justify-between mb-4" data-id="element-621">
                            <h3 className="text-lg font-bold text-purple-900" data-id="element-622">
                              Modifier la Station
                            </h3>
                            <div className="flex gap-2" data-id="element-623">
                              <button onClick={handleSaveEditStation} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors" data-id="element-624">
                                <SaveIcon className="w-4 h-4" data-id="element-625" />
                                Enregistrer
                              </button>
                              <button onClick={handleCancelEditStation} className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors" data-id="element-626">
                                Annuler
                              </button>
                            </div>
                          </div>
                          <StationForm formData={stationFormData} onInputChange={handleStationInputChange} onPriceChange={handlePriceChange} onAvailabilityChange={handleAvailabilityChange} data-id="element-627" />
                        </div> : <div data-id="element-628">
                          <div className="flex items-start justify-between mb-4" data-id="element-629">
                            <div className="flex-1" data-id="element-630">
                              <div className="flex items-center gap-2 mb-2" data-id="element-631">
                                <h3 className="font-bold text-gray-900 text-lg" data-id="element-632">
                                  {station.name}
                                </h3>
                                <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full" data-id="element-633">
                                  {station.code}
                                </span>
                                {station.isOperational ? <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-634" /> : <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-635" />}
                              </div>
                              <p className="text-sm text-gray-600" data-id="element-636">
                                {station.location}
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-637">
                                {station.commune}
                              </p>
                            </div>
                            <button onClick={() => handleStartEditStation(station)} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors" data-id="element-638">
                              <EditIcon className="w-4 h-4" data-id="element-639" />
                              Modifier
                            </button>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-640">
                            <div className="bg-gray-50 rounded-lg p-3" data-id="element-641">
                              <p className="text-xs text-gray-600 mb-1" data-id="element-642">
                                Pompes
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-643">
                                {station.pumps}
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3" data-id="element-644">
                              <p className="text-xs text-gray-600 mb-1" data-id="element-645">
                                Capacité
                              </p>
                              <p className="font-bold text-gray-900" data-id="element-646">
                                {(station.capacity / 1000).toFixed(0)}k L
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3" data-id="element-647">
                              <p className="text-xs text-gray-600 mb-1" data-id="element-648">
                                Essence
                              </p>
                              <p className="font-bold text-blue-900" data-id="element-649">
                                {station.prices.essence} FCFA
                              </p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3" data-id="element-650">
                              <p className="text-xs text-gray-600 mb-1" data-id="element-651">
                                Diesel
                              </p>
                              <p className="font-bold text-orange-900" data-id="element-652">
                                {station.prices.diesel} FCFA
                              </p>
                            </div>
                          </div>
                        </div>}
                    </div>)}
                </div> : <form onSubmit={handleSubmitNewStation} className="space-y-4" data-id="element-653">
                  <StationForm formData={stationFormData} onInputChange={handleStationInputChange} onPriceChange={handlePriceChange} onAvailabilityChange={handleAvailabilityChange} data-id="element-654" />
                  <div className="flex justify-end gap-3 pt-4" data-id="element-655">
                    <button type="button" onClick={() => {
                setStationSubTab('list');
                resetStationForm();
              }} className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors" data-id="element-656">
                      Annuler
                    </button>
                    <button type="submit" className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg" data-id="element-657">
                      <PlusIcon className="w-5 h-5" data-id="element-658" />
                      Ajouter la Station
                    </button>
                  </div>
                </form>}
            </div> : <div data-id="element-659">
              {/* User Sub-Tabs */}
              <div className="flex gap-2 mb-6" data-id="element-660">
                <button onClick={() => setUserSubTab('list')} className={`px-4 py-2 rounded-lg font-semibold transition-colors ${userSubTab === 'list' ? 'bg-purple-100 text-purple-900' : 'bg-gray-100 text-gray-600'}`} data-id="element-661">
                  Liste des Utilisateurs
                </button>
                <button onClick={() => {
              setUserSubTab('add');
              setEditingUser(null);
              resetUserForm();
            }} className={`px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2 ${userSubTab === 'add' ? 'bg-purple-100 text-purple-900' : 'bg-gray-100 text-gray-600'}`} data-id="element-662">
                  <PlusIcon className="w-4 h-4" data-id="element-663" />
                  Ajouter un Utilisateur
                </button>
              </div>
              {userSubTab === 'list' ? <div className="space-y-4" data-id="element-664">
                  {users.map(user => <div key={user.id} className={`border-2 rounded-xl p-5 ${editingUser === user.id ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`} data-id="element-665">
                      {editingUser === user.id ? <div className="space-y-4" data-id="element-666">
                          <div className="flex items-center justify-between mb-4" data-id="element-667">
                            <h3 className="text-lg font-bold text-purple-900" data-id="element-668">
                              Modifier l'Utilisateur
                            </h3>
                            <div className="flex gap-2" data-id="element-669">
                              <button onClick={handleSaveEditUser} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors" data-id="element-670">
                                <SaveIcon className="w-4 h-4" data-id="element-671" />
                                Enregistrer
                              </button>
                              <button onClick={handleCancelEditUser} className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors" data-id="element-672">
                                Annuler
                              </button>
                            </div>
                          </div>
                          <UserForm formData={userFormData} onInputChange={handleUserInputChange} stations={stations} isEditing={true} data-id="element-673" />
                        </div> : <div data-id="element-674">
                          <div className="flex items-start justify-between mb-4" data-id="element-675">
                            <div className="flex-1" data-id="element-676">
                              <div className="flex items-center gap-3 mb-2" data-id="element-677">
                                <UserIcon className="w-6 h-6 text-gray-600" data-id="element-678" />
                                <h3 className="font-bold text-gray-900 text-lg" data-id="element-679">
                                  {user.fullName}
                                </h3>
                                {getRoleBadge(user.role)}
                              </div>
                              <p className="text-sm text-gray-600 mb-1" data-id="element-680">
                                Identifiant:{' '}
                                <span className="font-semibold" data-id="element-681">
                                  {user.username}
                                </span>
                              </p>
                              {user.phone && <p className="text-sm text-gray-600 mb-1" data-id="element-682">
                                  Téléphone:{' '}
                                  <span className="font-semibold" data-id="element-683">
                                    {user.phone}
                                  </span>
                                </p>}
                              {user.address && <p className="text-sm text-gray-600 mb-1" data-id="element-684">
                                  Adresse:{' '}
                                  <span className="font-semibold" data-id="element-685">
                                    {user.address}
                                  </span>
                                </p>}
                              {user.assignedStationId && <p className="text-sm text-gray-600" data-id="element-686">
                                  Station assignée:{' '}
                                  <span className="font-semibold" data-id="element-687">
                                    {stations.find(s => s.id === user.assignedStationId)?.name}
                                  </span>
                                </p>}
                            </div>
                            <div className="flex gap-2" data-id="element-688">
                              <button onClick={() => handleStartEditUser(user)} className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors" data-id="element-689">
                                <EditIcon className="w-4 h-4" data-id="element-690" />
                                Modifier
                              </button>
                              {user.id !== currentUserId && <button onClick={() => {
                      if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur ${user.fullName}?`)) {
                        onDeleteUser(user.id);
                      }
                    }} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors" data-id="element-691">
                                  <TrashIcon className="w-4 h-4" data-id="element-692" />
                                  Supprimer
                                </button>}
                            </div>
                          </div>
                        </div>}
                    </div>)}
                </div> : <form onSubmit={handleSubmitNewUser} className="space-y-4" data-id="element-693">
                  <UserForm formData={userFormData} onInputChange={handleUserInputChange} stations={stations} isEditing={false} data-id="element-694" />
                  <div className="flex justify-end gap-3 pt-4" data-id="element-695">
                    <button type="button" onClick={() => {
                setUserSubTab('list');
                resetUserForm();
              }} className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors" data-id="element-696">
                      Annuler
                    </button>
                    <button type="submit" className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg" data-id="element-697">
                      <PlusIcon className="w-5 h-5" data-id="element-698" />
                      Ajouter l'Utilisateur
                    </button>
                  </div>
                </form>}
            </div>}
        </div>
      </div>
    </div>;
}
function StationForm({
  formData,
  onInputChange,
  onPriceChange,
  onAvailabilityChange
}: {
  formData: Partial<GasStation>;
  onInputChange: (field: string, value: any) => void;
  onPriceChange: (fuelType: 'essence' | 'diesel' | 'gpl', value: number) => void;
  onAvailabilityChange: (fuelType: 'essence' | 'diesel' | 'gpl', value: boolean) => void;
}) {
  return <div className="space-y-4" data-id="element-699">
      {/* Basic Info */}
      <div className="bg-gray-50 rounded-xl p-4" data-id="element-700">
        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-701">
          <MapPinIcon className="w-5 h-5 text-purple-600" data-id="element-702" />
          Informations Générales
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-703">
          <div data-id="element-704">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-705">
              Code Station *
            </label>
            <input type="text" value={formData.code || ''} onChange={e => onInputChange('code', e.target.value.toUpperCase())} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none uppercase" placeholder="Ex: TE-HAM" maxLength={10} required data-id="element-706" />
            <p className="text-xs text-gray-500 mt-1" data-id="element-707">
              Code unique pour identifier la station
            </p>
          </div>
          <div data-id="element-708">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-709">
              Nom de la Station *
            </label>
            <input type="text" value={formData.name || ''} onChange={e => onInputChange('name', e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" required data-id="element-710" />
          </div>
          <div data-id="element-711">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-712">
              Localisation *
            </label>
            <input type="text" value={formData.location || ''} onChange={e => onInputChange('location', e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" required data-id="element-713" />
          </div>
          <div data-id="element-714">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-715">
              Commune *
            </label>
            <select value={formData.commune || ''} onChange={e => onInputChange('commune', e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" required data-id="element-716">
              <option value="" data-id="element-717">Sélectionner...</option>
              <option value="Commune I" data-id="element-718">Commune I</option>
              <option value="Commune II" data-id="element-719">Commune II</option>
              <option value="Commune III" data-id="element-720">Commune III</option>
              <option value="Commune IV" data-id="element-721">Commune IV</option>
              <option value="Commune V" data-id="element-722">Commune V</option>
              <option value="Commune VI" data-id="element-723">Commune VI</option>
            </select>
          </div>
          <div data-id="element-724">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-725">
              Rive du Fleuve *
            </label>
            <select value={formData.riverSide || 'RG'} onChange={e => onInputChange('riverSide', e.target.value as 'RG' | 'RD')} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" required data-id="element-726">
              <option value="RG" data-id="element-727">Rive Gauche (RG)</option>
              <option value="RD" data-id="element-728">Rive Droite (RD)</option>
            </select>
          </div>
          <div data-id="element-729">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" data-id="element-730">
              <PhoneIcon className="w-4 h-4" data-id="element-731" />
              Contact Gérant *
            </label>
            <input type="tel" value={formData.managerContact || ''} onChange={e => onInputChange('managerContact', e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" placeholder="+223 XX XX XX XX" required data-id="element-732" />
          </div>
        </div>
      </div>
      {/* Technical Info */}
      <div className="bg-gray-50 rounded-xl p-4" data-id="element-733">
        <h4 className="font-bold text-gray-900 mb-3" data-id="element-734">
          Informations Techniques
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-735">
          <div data-id="element-736">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-737">
              Nombre de Pompes *
            </label>
            <input type="number" min="1" max="20" value={formData.pumps || 5} onChange={e => onInputChange('pumps', parseInt(e.target.value))} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" required data-id="element-738" />
          </div>
          <div data-id="element-739">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-740">
              Capacité (Litres) *
            </label>
            <input type="number" min="1000" step="1000" value={formData.capacity || 30000} onChange={e => onInputChange('capacity', parseInt(e.target.value))} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" required data-id="element-741" />
          </div>
          <div data-id="element-742">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" data-id="element-743">
              <ClockIcon className="w-4 h-4" data-id="element-744" />
              Horaires *
            </label>
            <input type="text" value={formData.openingHours || ''} onChange={e => onInputChange('openingHours', e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" placeholder="06:00 - 22:00" required data-id="element-745" />
          </div>
        </div>
      </div>
      {/* Fuel Prices */}
      <div className="bg-gray-50 rounded-xl p-4" data-id="element-746">
        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-747">
          <FuelIcon className="w-5 h-5 text-purple-600" data-id="element-748" />
          Prix et Disponibilité
        </h4>
        <div className="space-y-3" data-id="element-749">
          <div className="grid grid-cols-2 gap-4 items-center" data-id="element-750">
            <div data-id="element-751">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-752">
                Prix Essence (FCFA/L) *
              </label>
              <input type="number" min="0" step="5" value={formData.prices?.essence || 750} onChange={e => onPriceChange('essence', parseInt(e.target.value))} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" required data-id="element-753" />
            </div>
            <div className="flex items-center gap-2 mt-6" data-id="element-754">
              <input type="checkbox" checked={formData.fuelAvailability?.essence || false} onChange={e => onAvailabilityChange('essence', e.target.checked)} className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500" data-id="element-755" />
              <label className="text-sm font-semibold text-gray-700" data-id="element-756">
                Disponible
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 items-center" data-id="element-757">
            <div data-id="element-758">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-759">
                Prix Diesel (FCFA/L) *
              </label>
              <input type="number" min="0" step="5" value={formData.prices?.diesel || 680} onChange={e => onPriceChange('diesel', parseInt(e.target.value))} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" required data-id="element-760" />
            </div>
            <div className="flex items-center gap-2 mt-6" data-id="element-761">
              <input type="checkbox" checked={formData.fuelAvailability?.diesel || false} onChange={e => onAvailabilityChange('diesel', e.target.checked)} className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500" data-id="element-762" />
              <label className="text-sm font-semibold text-gray-700" data-id="element-763">
                Disponible
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 items-center" data-id="element-764">
            <div data-id="element-765">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-766">
                Prix GPL (FCFA/L)
              </label>
              <input type="number" min="0" step="5" value={formData.prices?.gpl || 520} onChange={e => onPriceChange('gpl', parseInt(e.target.value))} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" data-id="element-767" />
            </div>
            <div className="flex items-center gap-2 mt-6" data-id="element-768">
              <input type="checkbox" checked={formData.fuelAvailability?.gpl || false} onChange={e => onAvailabilityChange('gpl', e.target.checked)} className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500" data-id="element-769" />
              <label className="text-sm font-semibold text-gray-700" data-id="element-770">
                Disponible
              </label>
            </div>
          </div>
        </div>
      </div>
      {/* Operational Status */}
      <div className="bg-gray-50 rounded-xl p-4" data-id="element-771">
        <div className="flex items-center gap-3" data-id="element-772">
          <input type="checkbox" checked={formData.isOperational || false} onChange={e => onInputChange('isOperational', e.target.checked)} className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500" data-id="element-773" />
          <label className="text-sm font-semibold text-gray-700" data-id="element-774">
            Station Opérationnelle
          </label>
        </div>
      </div>
    </div>;
}
function UserForm({
  formData,
  onInputChange,
  stations,
  isEditing
}: {
  formData: Partial<User>;
  onInputChange: (field: string, value: any) => void;
  stations: GasStation[];
  isEditing: boolean;
}) {
  return <div className="space-y-4" data-id="element-775">
      {/* Basic Info */}
      <div className="bg-gray-50 rounded-xl p-4" data-id="element-776">
        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-777">
          <UserIcon className="w-5 h-5 text-purple-600" data-id="element-778" />
          Informations de l'Utilisateur
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-779">
          <div data-id="element-780">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-781">
              Nom Complet *
            </label>
            <input type="text" value={formData.fullName || ''} onChange={e => onInputChange('fullName', e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" placeholder="Ex: Mamadou Traoré" required data-id="element-782" />
          </div>
          <div data-id="element-783">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-784">
              Nom d'utilisateur *
            </label>
            <input type="text" value={formData.username || ''} onChange={e => onInputChange('username', e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" placeholder="Ex: mtraore" required data-id="element-785" />
          </div>
          <div data-id="element-786">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-787">
              Mot de passe *
            </label>
            <input type="password" value={formData.password || ''} onChange={e => onInputChange('password', e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" placeholder={isEditing ? 'Laisser vide pour ne pas changer' : 'Mot de passe'} required={!isEditing} data-id="element-788" />
          </div>
          <div data-id="element-789">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" data-id="element-790">
              <ShieldIcon className="w-4 h-4" data-id="element-791" />
              Rôle *
            </label>
            <select value={formData.role || 'operator'} onChange={e => onInputChange('role', e.target.value as UserRole)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" required data-id="element-792">
              <option value="operator" data-id="element-793">Opérateur</option>
              <option value="supervisor" data-id="element-794">Superviseur</option>
              <option value="general-supervisor" data-id="element-795">Superviseur Général</option>
              <option value="admin" data-id="element-796">Administrateur</option>
            </select>
          </div>
          <div data-id="element-797">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" data-id="element-798">
              <PhoneIcon className="w-4 h-4" data-id="element-799" />
              Numéro de Téléphone
            </label>
            <input type="tel" value={formData.phone || ''} onChange={e => onInputChange('phone', e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" placeholder="Ex: +223 76 12 34 56" data-id="element-800" />
          </div>
          <div data-id="element-801">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-802">
              Adresse
            </label>
            <input type="text" value={formData.address || ''} onChange={e => onInputChange('address', e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" placeholder="Ex: Bamako, ACI 2000" data-id="element-803" />
          </div>
        </div>
      </div>
      {/* Station Assignment */}
      {(formData.role === 'supervisor' || formData.role === 'operator') && <div className="bg-gray-50 rounded-xl p-4" data-id="element-804">
          <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-805">
            <MapPinIcon className="w-5 h-5 text-purple-600" data-id="element-806" />
            Station Assignée
          </h4>
          <div data-id="element-807">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-808">
              Sélectionner une station{' '}
              {formData.role === 'supervisor' || formData.role === 'operator' ? '*' : ''}
            </label>
            <select value={formData.assignedStationId || ''} onChange={e => onInputChange('assignedStationId', e.target.value || undefined)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none" required={formData.role === 'supervisor' || formData.role === 'operator'} data-id="element-809">
              <option value="" data-id="element-810">Aucune station</option>
              {stations.map(station => <option key={station.id} value={station.id} data-id="element-811">
                  {station.name} - {station.location}
                </option>)}
            </select>
            <p className="text-xs text-gray-500 mt-2" data-id="element-812">
              {formData.role === 'supervisor' ? 'Les superviseurs peuvent gérer les véhicules de leur station assignée' : formData.role === 'operator' ? 'Les opérateurs peuvent ajouter des véhicules à leur station assignée' : formData.role === 'general-supervisor' ? 'Les superviseurs généraux ont accès en lecture seule à toutes les stations' : 'Les administrateurs ont accès à toutes les stations'}
            </p>
          </div>
        </div>}
    </div>;
}